const html = document.documentElement;
const body = document.body;

const maxNumberOfFilterCheckboxesToShowInitially = 10;

/**
 * Shared product-listing behaviour for the category, supplier, brokerage and
 * search pages. The markup and filter logic are identical across those pages;
 * the only per-page difference is the request URL used to fetch the filtered
 * listing JSON (the category-family endpoints key off a "&json" query param,
 * the search endpoint relies on the Accept header alone).
 *
 * @param {Object} config
 * @param {string} [config.jsonSuffix='']  Appended to the fetch URL only (not the history URL).
 * @param {string} [config.name='listing'] Label used for debug logging.
 */
export function initBrokerageListing(config = {})
{
    const { jsonSuffix = '', name = 'listing' } = config;

    console.log(name + " listing loaded");

    let sortSelects;
    let apply_filters_timeout;

    document.addEventListener("DOMContentLoaded", () => {
        const productListContainer = document.getElementById("product_list_container");
        const productList = document.getElementById("product_list");
        const filterForm = document.getElementById("filter_form");
        const baseUrl = productListContainer.dataset.url;
        const url = new URL(baseUrl);

        function loadListingJson(filters)
        {
            url.search = toQueryString(filters);
            const requestUrl = url.toString();

            window.history.replaceState({}, '', requestUrl);

            return fetch(requestUrl + jsonSuffix, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                // body: JSON.stringify(filters)
            })
            .then(response => {
                if (!response.ok) {
                    //throw new Error(`HTTP error! Status: ${response.status}`);
                    window.modal("Sorry, there was a problem loading the product list, please try again.","Sorry");
                    productListContainer.classList.remove("loading");
                    return false;
                }
                return response.json();
            })
            .then(data => {
                console.log('Listing JSON Loaded');
                return data;
            });
        }

        // Wait for the JSON to load
        async function readListingJson(filters)
        {
            const data = await loadListingJson(filters);
            return data;
        }

        /**
         * Rebuilds the grid from the product JSON alone, which is also what
         * takes the search page's bundle cards out of it: the server only
         * deals those in on an unnarrowed search, so filtering or sorting is
         * meant to lose them (see SearchController).
         */
        async function applyFilters()
        {
            const filters = window.serialize(filterForm);
            filters.sort = sortSelects[0].value;
            let new_products = [];
            let pagination = document.getElementById("pagination");
            let item_count = document.getElementById("item_count");
            productListContainer.classList.add("loading");
            pagination.classList.add("hidden");

            const data = await readListingJson(filters);

            //empty product list
            productList.innerHTML = "";

            if (data)
            {
                if (data.hasOwnProperty("data") && data.data.hasOwnProperty("products"))
                {
                    data.data.products.forEach(product => {
                        new_products.push(buildProductListItem(product));
                    });
                }

                if (data.hasOwnProperty("meta") && data.meta.hasOwnProperty("total"))
                {
                    item_count.innerHTML = data.meta.total + "  product" + (data.meta.total!=1 ? "s" : "");
                }
            }
            else
            {
                console.log(data);
            }

            if (new_products.length)
            {
                //productList.replaceChildren(new_products);

                new_products.forEach(new_product => {
                    productList.appendChild(new_product);
                });
            }
            else
            {
                productList.innerHTML = "<p class=\"no_products\">There are No Products to Show</p>";
            }

            if (data.hasOwnProperty("pagination"))
            {
                pagination.innerHTML = data.pagination;
            }

            productListContainer.classList.remove("loading");
            pagination.classList.remove("hidden");
        }

        function readyToApplyFilters(delay = 1000)
        {
            console.log("Preparing to apply filters");
            clearTimeout(apply_filters_timeout);
            apply_filters_timeout = setTimeout(applyFilters, delay);
        }

        function buildProductListItem(product)
        {
            const template = document.querySelector("#templates .product_list_item");
            let ProductListItem = template.cloneNode(true);
            let link = ProductListItem.querySelector("a");
            let img = ProductListItem.querySelector(".image_container > img");
            let banners = ProductListItem.querySelector(".product_banners");
            let badges = ProductListItem.querySelector(".product_badges");
            let heading = ProductListItem.querySelector("h3");
            let code = ProductListItem.querySelector(".code");
            let colours = ProductListItem.querySelector(".colours");
            let exVatPrice = ProductListItem.querySelector(".ex_vat_price");
            let incVatPrice = ProductListItem.querySelector(".inc_vat_price");
            let schemaContainer = ProductListItem.querySelector("script[type='application/ld+json']");

            link.setAttribute("href",product.url);
            if (product.hasOwnProperty("thumb"))
            {
                img.setAttribute("src",product.thumb);
                img.setAttribute("alt",product.name);
            }
            else
            {
                img.remove();
            }
            if (product.hasOwnProperty("banners") && Array.isArray(product.banners) && product.banners.length)
            {
                product.banners.forEach(banner => {
                    const banner_span = document.createElement("span");
                    banner_span.className = banner.class ?? "";
                    banner_span.textContent = banner.label ?? "";
                    banners.appendChild(banner_span);
                });
            }
            if (product.hasOwnProperty("badges") && Array.isArray(product.badges) && product.badges.length)
            {
                product.badges.forEach(badge => {
                    if (badge.label!="")
                    {
                        const badge_span = document.createElement("span");
                        badge_span.classList.add("badge");
                        badge_span.innerHTML = badge.label;
                        if (badge.hasOwnProperty("short_description") && badge.short_description != "")
                        {
                            badge_span.setAttribute("title",badge.short_description ?? '');
                        }
                        if (badge.hasOwnProperty("background_hex") || badge.hasOwnProperty("text_hex"))
                        {
                            let style_attribute = "";
                            if (badge.hasOwnProperty("background_hex") && badge.background_hex != "")
                            {
                                style_attribute+="background-color: #"+badge.background_hex+";";
                            }
                            if (badge.hasOwnProperty("text_hex") && badge.text_hex != "")
                            {
                                style_attribute+="color: #"+badge.text_hex+";";
                            }
                            badge_span.setAttribute("style",style_attribute);
                        }

                        badges.appendChild(badge_span);
                    }
                });
            }

            heading.innerHTML = product.name;
            code.innerHTML = product.code;

            // Category-family listings send a swatch payload (swatches + true
            // total, capped server-side); the search listing still sends a
            // plain count until its resource is converted, so keep that branch.
            if (colours)
            {
                if (product.swatches && product.swatches.total > 0)
                {
                    if (product.swatches.swatches.length)
                    {
                        product.swatches.swatches.forEach(swatch => {
                            const swatchSpan = document.createElement("span");
                            swatchSpan.className = "colour_swatch " + swatch.contrast + "_contrast" + (swatch.multi ? " multi_colour" : "");
                            swatchSpan.setAttribute("style", swatch.style);
                            swatchSpan.setAttribute("title", swatch.label);
                            if (swatch.anchor)
                            {
                                swatchSpan.setAttribute("data-anchor", swatch.anchor);
                            }
                            if (swatch.thumbnail)
                            {
                                swatchSpan.setAttribute("data-thumbnail", swatch.thumbnail);
                            }
                            colours.appendChild(swatchSpan);
                        });

                        const overflow = product.swatches.total - product.swatches.swatches.length;
                        if (overflow > 0)
                        {
                            const moreSpan = document.createElement("span");
                            moreSpan.className = "more_colours";
                            moreSpan.textContent = "+" + overflow;
                            colours.appendChild(moreSpan);

                            const iconSpan = document.createElement("span");
                            iconSpan.className = "icon_text swatch";
                            colours.appendChild(iconSpan);
                        }
                    }
                    else
                    {
                        // No colour had usable hex data: fall back to the count
                        // text, mirroring the Blade component's behaviour.
                        const countSpan = document.createElement("span");
                        countSpan.className = "icon_text large_icon swatch";
                        countSpan.textContent = product.swatches.total + " colour" + (product.swatches.total != 1 ? "s" : "");
                        colours.appendChild(countSpan);
                    }
                }
                else if (product.hasOwnProperty("colours") && product.colours > 0)
                {
                    // Count fallback: search documents that predate the swatch
                    // payload (i.e. not yet reindexed) only carry a count.
                    let countSpan = colours.querySelector(".icon_text");
                    if (!countSpan)
                    {
                        countSpan = document.createElement("span");
                        countSpan.className = "icon_text large_icon swatch";
                        colours.appendChild(countSpan);
                    }
                    countSpan.innerHTML = product.colours + " colour" + (product.colours != 1 ? "s" : "");
                }
                else
                {
                    colours.remove();
                }
            }

            exVatPrice.setAttribute("data-raw",product.ex_vat_price);
            exVatPrice.querySelector(".ex_vat_value").innerHTML = window.displayPrice(product.ex_vat_price);
            incVatPrice.setAttribute("data-raw",product.inc_vat_price);
            incVatPrice.querySelector(".inc_vat_value").innerHTML = window.displayPrice(product.inc_vat_price);

            // The category-family templates include an ld+json schema script; the
            // search template does not, so this block is a no-op there.
            if (schemaContainer)
            {
                schemaContainer.innerHTML = product.schema;
            }

            window.fadeLazyLoadImage(ProductListItem);

            return ProductListItem;
        }


        function changeSortOrder(sortBy)
        {
            sortSelects.forEach(sortSelect => {
                sortSelect.value = sortBy;
            });

            readyToApplyFilters(0);
        }

        function getFieldLabel(field) {
            // Check for a <label> with a "for" attribute matching the field's id
            let label = document.querySelector(`label[for="${field.id}"]`);
            if (label) {
                return label.innerText.trim();
            }

            // Check if the field is inside a <label> tag
            label = field.closest("label");
            if (label) {
                return label.innerText.trim();
            }

            return null; // Return null if no label is found
        }

        function clearFilterField(field)
        {
            if (field.type=="checkbox")
            {
                field.checked = false;
            }
            else if (field.type === "range")
            {
                if (field.name=="price_range_min")
                {
                    field.value = field.min;
                }
                else if (field.name=="price_range_max")
                {
                    field.value = field.max;
                }
                field.dispatchEvent(new Event("input", { bubbles: true }));
            }
            field.dispatchEvent(new Event("change", { bubbles: true }));
        }

        function findFilterField(name, value = null)
        {
            const form = document.querySelector("#filter_form");
            const field = form.querySelector(`[name="${name}"]`);

            if (field)
            {
                // If it's a checkbox and a value is provided, find the specific checkbox
                if (field.type === "checkbox" && value !== null)
                {
                    return form.querySelector(`[name="${name}"][value="${value}"]`);
                }
                return field;
            }

            return null;
        }

        function updateActiveFilters()
        {
            const formData = new FormData(filterForm);
            const formElements = filterForm.elements;
            const activeFilters = document.querySelectorAll(".active_filters");
            const filterCounts = document.querySelectorAll(".filter_count.total");
            const clearAllButtons = filterForm.querySelectorAll(".chip.clear_all:not(.global)");
            let chips = [];
            let activeFormSections = [];
            for (const field of formElements)
            {
                let field_active = false;
                const formSection = field.closest(".pop_out_menu_section");
                let chip = {};
                if ((field.type === "checkbox" || field.type === "radio") && field.checked)
                {
                    chip.label = getFieldLabel(field);
                    field_active = true;
                }
                if (field.type === "range")
                {
                    if (field.name=="price_range_min")
                    {
                        if (field.value!=field.min)
                        {
                            chip.label = "Min £"+field.value;
                            field_active = true;
                        }
                    }
                    else if (field.name=="price_range_max")
                    {
                        if (field.value!=field.max)
                        {
                            chip.label = "Max £"+field.value;
                            field_active = true;
                        }
                    }
                }
                if (field_active)
                {
                    activeFormSections.indexOf(formSection) === -1 ? activeFormSections.push(formSection) : null;
                    chip.name = field.name;
                    chip.value = field.value;
                    chips.push(chip);
                }
            }

            activeFilters.forEach(activeFilter => {
                activeFilter.innerHTML = "";
            });
            if (chips.length)
            {
                chips.forEach(chip => {
                    activeFilters.forEach(activeFilter => {
                        const div = document.createElement("div");
                        div.setAttribute("role","button");
                        div.setAttribute("data-name",chip.name);
                        div.setAttribute("data-value",chip.value);
                        div.classList.add("chip","clear");
                        div.textContent = chip.label;
                        activeFilter.appendChild(div);
                    });
                });
                const clearAll = document.createElement("div");
                clearAll.setAttribute("role","button");
                clearAll.classList.add("chip","clear_all","global");
                clearAll.textContent = "Clear all";
                document.querySelector(".active_filters.above_listing").appendChild(clearAll);
            }
            clearAllButtons.forEach(clearAllButton => { clearAllButton.classList.add("hidden"); });
            if (activeFormSections.length)
            {
                activeFormSections.forEach(activeFormSection => {
                    const clear_all = activeFormSection.querySelector(".chip.clear_all");
                    if (clear_all)
                    {
                        clear_all.classList.remove("hidden");
                    }
                });
            }
            filterCounts.forEach(filterCount => {
                filterCount.innerHTML = chips.length;
                if (chips.length==0)
                {
                    document.querySelector(".active_filters_outer").classList.add("inactive");
                    filterCount.classList.add("hidden");
                }
                else
                {
                    document.querySelector(".active_filters_outer").classList.remove("inactive");
                    filterCount.classList.remove("hidden");
                }
            });
        }

        function toQueryString(params) {
            const query = new URLSearchParams();

            for (const key in params) {
              const value = params[key];

              if (Array.isArray(value)) {
                value.forEach(val => query.append(`${key}`, val));
              } else {
                query.append(key, value);
              }
            }

            return query.toString();
          }

        //sticky filter behaviour

        window.addEventListener('scroll', () => {
            // Get the current vertical scroll position
            const scrollTop = window.scrollY || html.scrollTop;
            const stickyFilter = document.querySelector(".product_list_container .sticky_list_controls");
            const listHeader = document.querySelector(".list_header");
            const showFilterAt = listHeader.getBoundingClientRect().top + window.scrollY;

            if (scrollTop > showFilterAt)
            {
                stickyFilter.classList.add("visible");
            }
            else
            {
                stickyFilter.classList.remove("visible");
            }

        });

        document.addEventListener('click',(event) => {
            if (event.target)
            {
                const target = event.target;
                if (target.classList.contains("show_all_checkbox_list"))
                {
                    if(target.previousElementSibling)
                    {
                        target.previousElementSibling.classList.add("show_all");
                    }
                }
            }
        });

        sortSelects = document.querySelectorAll("select.sort");

        sortSelects.forEach(sortSelect => {
            sortSelect.addEventListener('change',() => {
                changeSortOrder(sortSelect.value);
            });
        });

        const filterMenuCheckboxLists = document.querySelectorAll(".filters_menu .checkbox_list");

        filterMenuCheckboxLists.forEach(checkboxList => {
            if (checkboxList.querySelectorAll("li").length > maxNumberOfFilterCheckboxesToShowInitially)
            {
                const numberNotShown = checkboxList.querySelectorAll("li").length - maxNumberOfFilterCheckboxesToShowInitially;
                const showAllHTML = "<a class=\"show_all_checkbox_list\">"+numberNotShown+" more...</a>";
                checkboxList.insertAdjacentHTML("afterend",showAllHTML);
            }
        });

        filterForm.addEventListener("change", () => {
            readyToApplyFilters();
            updateActiveFilters();
        });
        updateActiveFilters();

        document.querySelectorAll(".filters_menu .pop_out_menu_section").forEach(section => {
            if (section.querySelector("input:checked"))
            {
                section.classList.add("open");
            }
        });

        //TO DO: add behaviour for clicking to remove chips
        document.addEventListener('click',(event) => {
            if (event.target)
            {
                const target = event.target;
                if (target.classList.contains("chip") && target.classList.contains("clear"))
                {
                    const value = target.getAttribute("data-value");
                    const name = target.getAttribute("data-name");
                    const field = findFilterField(name, value);
                    if (field)
                    {
                        clearFilterField(field);
                    }
                }
                else if (target.closest(".filters_menu .pop_out_menu_section") && !target.classList.contains("clear_all") && !target.classList.contains("show_all_checkbox_list") && !target.closest(".checkbox_list"))
                {
                    const section = target.closest(".filters_menu .pop_out_menu_section");
                    section.classList.toggle("open");
                }
                else if (target.classList.contains("chip") && target.classList.contains("clear_all"))
                {
                    if (target.classList.contains("global"))
                    {
                        //find all chips
                        const all_active_chips = document.querySelectorAll(".active_filters.above_listing .chip.clear");
                        console.log(all_active_chips);
                        all_active_chips.forEach(active_chip => {
                            const value = active_chip.getAttribute("data-value");
                            const name = active_chip.getAttribute("data-name");
                            const field = findFilterField(name, value);
                            if (field)
                            {
                                clearFilterField(field);
                            }
                        });
                        readyToApplyFilters(0); //now instantly reload
                    }
                    else
                    {
                        const formSection = target.closest(".pop_out_menu_section");
                        if (formSection)
                        {
                            const allFields = formSection.querySelectorAll(`input[name],textarea[name],select[name]`);
                            if (allFields.length)
                            {
                                allFields.forEach(field => {
                                    clearFilterField(field);
                                });
                                readyToApplyFilters(0); //now instantly reload
                            }
                        }
                    }
                }
            }
        });


    });
}
